/**
 * LoginPage - Production Landing Page
 * 
 * Based on xp1_HybridD_v3 "Depth Cinema V3-HD"
 * Full visual fidelity version - all effects enabled.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useLanguage } from '@/i18n';
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import './LoginPage.css';

// Desktop Images
const overviewImg = '/showcase/overview_page.png';
const accountsImg = '/showcase/accounts_page.png';
const analyticsImg = '/showcase/ana_page.png';
const categoryImg = '/showcase/category_page.png';
const settingsImg = '/showcase/settings_page.jpg';

// Mobile Images
const mobileOverviewImg = '/showcase/mobile_overview.png';
const mobileAccountsImg = '/showcase/mobile_accounts.png';
const mobileAnalyticsImg = '/showcase/mobile_ana.png';
const mobileSettingsImg = '/showcase/mobile_settings.png';

// Responsive image helper
interface ResponsiveImg {
  desktop: string;
  mobile: string;
}

// i18n: title/subtitle/desc are catalog keys (login.card*), t() at render.
const DEPTH_CARDS = [
  {
    id: 'visualize',
    num: '01',
    title: 'login.card1_title',
    subtitle: 'login.card1_subtitle',
    desc: 'login.card1_desc',
    img: { desktop: overviewImg, mobile: mobileOverviewImg } as ResponsiveImg,
    color: '#7aa2f7',
  },
  {
    id: 'analyze',
    num: '02',
    title: 'login.card2_title',
    subtitle: 'login.card2_subtitle',
    desc: 'login.card2_desc',
    img: { desktop: analyticsImg, mobile: mobileAnalyticsImg } as ResponsiveImg,
    color: '#bb9af7',
  },
  {
    id: 'organize',
    num: '03',
    title: 'login.card3_title',
    subtitle: 'login.card3_subtitle',
    desc: 'login.card3_desc',
    img: { desktop: categoryImg, mobile: mobileAnalyticsImg } as ResponsiveImg,
    color: '#9ece6a',
  },
  {
    id: 'optimize',
    num: '04',
    title: 'login.card4_title',
    subtitle: 'login.card4_subtitle',
    desc: 'login.card4_desc',
    img: { desktop: settingsImg, mobile: mobileSettingsImg } as ResponsiveImg,
    color: '#e0af68',
  },
];

// i18n: quote/role are catalog keys (login.testimonial*), t() at render;
// author names stay as-is (proper nouns).
const TESTIMONIALS = [
  {
    quote: 'login.testimonial1_quote',
    author: 'Hana K.',
    role: 'login.testimonial1_role',
  },
  {
    quote: 'login.testimonial2_quote',
    author: 'Kazuki M.',
    role: 'login.testimonial2_role',
  },
  {
    quote: 'login.testimonial3_quote',
    author: 'Priya S.',
    role: 'login.testimonial3_role',
  },
];

export function LoginPage() {
  const { t } = useLanguage();
  const { signInWithGoogle, isLoading } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    
    // Check if mobile
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // NON-THROTTLED scroll handler - full effects
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleGoogleSignIn = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      console.error('[LOGIN] Failed to sign in:', error.message);
    }
  };

  const heroImages = isMobile 
    ? [mobileAccountsImg, mobileOverviewImg, mobileAnalyticsImg]
    : [accountsImg, overviewImg, analyticsImg];
  const heroOpacity = Math.max(0, 1 - scrollY / 600);
  const heroScale = Math.max(0.8, 1 - scrollY / 3000);

  return (
    <div className={`login-page ${mounted ? 'mounted' : ''}`} ref={containerRef}>
      {/* Background Glow */}
      <div className="login-bg-glow" />

      {/* Header */}
      <header className="login-header">
        <div className="login-logo">
          <span className="login-logo-symbol">$</span>
          <span className="login-logo-text">NINJACASH</span>
        </div>
        <nav className="login-nav">
          <PublicLanguageSelect />
          <Link to="/features" className="login-nav-link">{t('login.nav_features')}</Link>
          <button
            className="login-nav-cta"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? t('common.loading') : t('login.launch')}
          </button>
        </nav>
      </header>

      {/* Cinematic Hero */}
      <section 
        className="login-hero"
        style={{ 
          opacity: heroOpacity,
          transform: `scale(${heroScale})`,
        }}
      >
        <div className="login-hero-content">
          <div className="login-hero-tag">
            <span className="login-tag-pulse" />
            {t('login.badge')}
          </div>

          <h1 className="login-hero-title">
            <span className="sr-only">NinjaCash - Personal Finance Tracker</span>
            <span className="login-title-line login-anim-1" aria-hidden="true">NINJA</span>
            <span className="login-title-line login-title-glow login-anim-2" aria-hidden="true">CASH</span>
          </h1>

          <p className="login-hero-subtitle login-anim-3">
            {t('login.tagline')}
          </p>

          <p className="login-hero-desc login-anim-4">
            {t('login.pitch')}
          </p>

          <div className="login-hero-actions login-anim-5">
            <button
              className="login-btn-primary"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isLoading ? t('login.entering') : t('login.enter')}
            </button>
          </div>

          {/* Enhanced Stats */}
          <div className="login-hero-stats login-anim-6">
            <div className="login-stat">
              <span className="login-stat-value">100%</span>
              <span className="login-stat-label">{t('login.stat_offline')}</span>
            </div>
            <div className="login-stat-divider" />
            <div className="login-stat">
              <span className="login-stat-value">64+</span>
              <span className="login-stat-label">{t('login.stat_categories')}</span>
            </div>
            <div className="login-stat-divider" />
            <div className="login-stat">
              <span className="login-stat-value">9</span>
              <span className="login-stat-label">{t('login.stat_themes')}</span>
            </div>
          </div>

          {/* Developer Credit */}
          <a 
            href="https://github.com/TakashiKyoto/NinjaCash" 
            target="_blank" 
            rel="noopener noreferrer"
            className="login-dev-credit login-anim-6"
          >
            {t('login.dev_credit')}
          </a>
        </div>

        {/* 3D Device - responsive: desktop shows wider frame, mobile shows phone frame */}
        <div className={`login-hero-device ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
          <div className="login-device-wrapper">
            <div className="login-device-frame">
              {heroImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="App preview"
                  className={`login-device-img ${i === activeImg ? 'active' : ''}`}
                />
              ))}
            </div>
            <div className="login-device-glow" />
            <div className="login-device-reflection" />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="login-scroll-hint">
          <span>{t('login.scroll')}</span>
          <div className="login-scroll-line" />
        </div>
      </section>

      {/* Stream Section with Central Line */}
      <section className="login-stream">
        <div className="login-stream-line" />

        {/* Depth Cards */}
        <div className="login-cards-container">
          {DEPTH_CARDS.map((card, index) => (
            <div
              key={card.id}
              className="login-card-wrapper"
              style={{ 
                '--card-color': card.color,
                '--card-index': index,
              } as React.CSSProperties}
            >
              <div className="login-card">
                <div className="login-card-content">
                  <div className="login-card-num">{card.num}</div>
                  <h2 className="login-card-title">{t(card.title)}</h2>
                  <h3 className="login-card-subtitle">{t(card.subtitle)}</h3>
                  <p className="login-card-desc">{t(card.desc)}</p>
                </div>
                <div className="login-card-visual">
                  <div className="login-card-img-frame">
                    <picture>
                      <source media="(max-width: 768px)" srcSet={card.img.mobile} />
                      <img src={card.img.desktop} alt={t(card.title)} className="login-card-img" />
                    </picture>
                  </div>
                </div>
                <div className="login-card-glow" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="login-testimonials">
        <h2 className="login-section-title">{t('login.testimonials_title')}</h2>
        <div className="login-testimonial-container">
          {TESTIMONIALS.map((tm, i) => (
            <div
              key={i}
              className={`login-testimonial ${i === activeTestimonial ? 'active' : ''}`}
            >
              <blockquote className="login-testimonial-quote">
                "{t(tm.quote)}"
              </blockquote>
              <div className="login-testimonial-author">
                <span className="login-testimonial-name">{tm.author}</span>
                <span className="login-testimonial-role">{t(tm.role)}</span>
              </div>
            </div>
          ))}
          <div className="login-testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                className={`login-testimonial-dot ${i === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(i)}
                aria-label={`View testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="login-cta">
        <div className="login-cta-content">
          <h2 className="login-cta-title">
            <span className="login-title-glow">READY?</span>
          </h2>
          <p className="login-cta-desc">
            {t('login.footer_pitch')}
          </p>
          <button
            className="login-btn-primary login-btn-large"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? t('login.initializing') : t('login.initialize')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="login-footer">
        <div className="login-footer-brand">
          <span className="login-logo-symbol login-logo-small">$</span>
          <span>NINJACASH</span>
        </div>
        <div className="login-footer-links">
          <Link to="/features">{t('login.nav_features')}</Link>
          <Link to="/privacy">{t('settings.legal.privacy')}</Link>
          <Link to="/terms">{t('settings.legal.terms')}</Link>
          <a href="https://github.com/TakashiKyoto/NinjaCash" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </div>
        <div className="login-footer-copy">
          &copy; {new Date().getFullYear()} NinjaCash
        </div>
      </footer>
    </div>
  );
}

export default LoginPage;
